import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ADDED_FEEDBACK_MS = 2000;

/**
 * "Shop the feed" section: scroll-snap slider arrows, lazy post videos,
 * and a native <dialog> per post to add the tagged products to the cart.
 */
class FeedSocialComponent extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver[]} */
  #observers = [];

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('submit', this.#onSubmit, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });

    this.#observeHeading();
    this.#setupArrows();
    this.#setupVideos();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-feed-track]'));
  }

  /** Draws the title underline the first time the section scrolls into view. */
  #observeHeading() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.classList.add('is-inview');
        observer.disconnect();
      },
      { threshold: 0.3 }
    );
    observer.observe(this);
    this.#observers.push(observer);
  }

  #setupArrows() {
    const track = this.#track;
    const prev = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-feed-prev]'));
    const next = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-feed-next]'));
    const cards = track ? Array.from(track.children) : [];
    const first = cards[0];
    const last = cards[cards.length - 1];
    if (!track || !prev || !next || !first || !last) return;

    // Edge cards fully visible ⇒ that arrow is useless. Cheaper than listening to scroll.
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const atEdge = entry.intersectionRatio > 0.95;
          if (entry.target === first) prev.disabled = atEdge;
          if (entry.target === last) next.disabled = atEdge;
        }
      },
      { root: track, threshold: [0, 0.95, 1] }
    );
    observer.observe(first);
    if (last !== first) observer.observe(last);
    this.#observers.push(observer);
  }

  /** @param {1 | -1} direction */
  #scrollBy(direction) {
    const track = this.#track;
    const card = /** @type {HTMLElement | null} */ (track?.firstElementChild ?? null);
    if (!track || !card) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const perView = Math.max(1, Math.floor((track.clientWidth + gap) / (card.offsetWidth + gap)));
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({ left: direction * rtl * perView * (card.offsetWidth + gap) });
  }

  #setupVideos() {
    const posts = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-feed-video]')));
    if (!posts.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const saveData = /** @type {any} */ (navigator).connection?.saveData === true;
    if (reducedMotion || saveData) return;

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    const playOnHover = canHover && this.dataset.videoMode !== 'inview';

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const post = /** @type {HTMLElement} */ (entry.target);
          if (entry.isIntersecting) {
            const video = this.#mountVideo(post);
            if (!playOnHover && video) this.#play(post, video);
          } else {
            this.#pause(post);
          }
        }
      },
      // Mount a bit before the card is visible so hover playback starts instantly
      { rootMargin: '200px', threshold: playOnHover ? 0 : 0.6 }
    );
    posts.forEach((post) => observer.observe(post));
    this.#observers.push(observer);

    if (!playOnHover) return;

    const signal = this.#abort?.signal;
    for (const post of posts) {
      post.addEventListener(
        'pointerenter',
        () => {
          const video = this.#mountVideo(post);
          if (video) this.#play(post, video);
        },
        { signal }
      );
      post.addEventListener('pointerleave', () => this.#pause(post), { signal });
    }
  }

  /**
   * Moves the <video> out of its inert <template> the first time it's needed.
   * @param {HTMLElement} post
   * @returns {HTMLVideoElement | null}
   */
  #mountVideo(post) {
    const existing = post.querySelector('video');
    if (existing) return existing;

    const template = /** @type {HTMLTemplateElement | null} */ (post.querySelector('[data-feed-video-template]'));
    if (!template) return null;

    // importNode (not a move) so the element belongs to this document and runs source selection here
    const fragment = document.importNode(template.content, true);
    const video = fragment.querySelector('video');
    if (!video) return null;

    video.muted = true;
    template.replaceWith(fragment);
    return video;
  }

  /**
   * @param {HTMLElement} post
   * @param {HTMLVideoElement} video
   */
  #play(post, video) {
    video.preload = 'auto';
    video
      .play()
      .then(() => post.classList.add('is-playing'))
      .catch(() => {
        // Autoplay can be refused (e.g. low-power mode); the photo stays visible.
      });
  }

  /** @param {HTMLElement} post */
  #pause(post) {
    post.querySelector('video')?.pause();
    post.classList.remove('is-playing');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {Element} */ (event.target);

    if (target.closest('[data-feed-prev]')) return this.#scrollBy(-1);
    if (target.closest('[data-feed-next]')) return this.#scrollBy(1);

    const opener = /** @type {HTMLElement | null} */ (target.closest('[data-feed-open]'));
    if (opener) return this.#openLook(opener);

    const closeButton = target.closest('[data-feed-close]');
    if (closeButton) return closeButton.closest('dialog')?.close();

    // Click on the ::backdrop lands on the <dialog> element itself
    if (target instanceof HTMLDialogElement && target.classList.contains('feed-look')) {
      target.close();
    }
  };

  /** @param {HTMLElement} opener */
  #openLook(opener) {
    const dialog = /** @type {HTMLDialogElement | null} */ (
      document.getElementById(opener.dataset.feedOpen ?? '')
    );
    if (!dialog || dialog.open) return;

    dialog.showModal();
    dialog.scrollTop = 0;

    const index = opener.dataset.feedIndex;
    if (index === undefined) return;

    const item = /** @type {HTMLElement | undefined} */ (dialog.querySelectorAll('[data-feed-item]')[Number(index)]);
    if (!item || index === '0') return;

    item.scrollIntoView({ block: 'start' });
    item.classList.remove('is-highlighted');
    requestAnimationFrame(() => item.classList.add('is-highlighted'));
  }

  /** Keeps price and button state in sync with the chosen size/variant. */
  #onChange = (/** @type {Event} */ event) => {
    const select = /** @type {HTMLElement} */ (event.target);
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-feed-variant]')) return;

    const option = select.selectedOptions[0];
    const item = select.closest('[data-feed-item]');
    if (!option || !item) return;

    const current = item.querySelector('[data-feed-price-current]');
    const compare = /** @type {HTMLElement | null} */ (item.querySelector('[data-feed-price-compare]'));

    if (current && option.dataset.price) current.textContent = option.dataset.price;
    if (compare) {
      compare.textContent = option.dataset.compare ?? '';
      compare.hidden = !option.dataset.compare;
    }
    this.#setError(item, '');
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form.matches('[data-feed-add-form]')) return;

    event.preventDefault();
    this.#addToCart(form);
  };

  /**
   * Mirrors product-form.js so the cart drawer and cart icon react exactly as they
   * do for the product page (the drawer opens once this dialog closes).
   * @param {HTMLFormElement} form
   */
  async #addToCart(form) {
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    if (!button || button.getAttribute('aria-busy') === 'true') return;

    const formData = new FormData(form);
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: /** @type {string} */ (formData.get('id')), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    button.setAttribute('aria-busy', 'true');
    this.#setError(form, '');

    try {
      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: { ...fetchCfg.headers, Accept: 'text/html' },
      });
      const result = await response.json();
      const cart = await this.#fetchCart();

      if (result.status) {
        const message = result.description || result.message || 'Error';
        this.#setError(form, message);
        form.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'feed-social' },
        });
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'feed-social',
          itemCount: 1,
          sections: result.sections,
        },
      });

      this.#showAdded(button);
      // The cart drawer waits for this modal to close before opening itself.
      form.closest('dialog')?.close();
    } catch (error) {
      deferred.reject(error);
      this.#setError(form, error instanceof Error ? error.message : 'Network error');
      form.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /** @returns {Promise<any>} */
  async #fetchCart() {
    /** @type {any} */
    const cartItems = document.querySelector('cart-items-component');
    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /** @param {HTMLButtonElement} button */
  #showAdded(button) {
    const label = button.textContent ?? '';
    button.textContent = Theme.translations.added || label;
    setTimeout(() => {
      button.textContent = label;
    }, ADDED_FEEDBACK_MS);
  }

  /**
   * @param {Element} scope
   * @param {string} message
   */
  #setError(scope, message) {
    const error = /** @type {HTMLElement | null} */ (scope.querySelector('[data-feed-error]'));
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /** Theme editor: bring the selected post into view. */
  #onBlockSelect = (/** @type {Event} */ event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!this.contains(target)) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
  };
}

if (!customElements.get('feed-social-component')) {
  customElements.define('feed-social-component', FeedSocialComponent);
}
